// @ts-check
// Building a PATCH body, which on this API is the single most destructive thing
// a client can get wrong.
//
// Every PATCH body uses tri-state Opt<T> fields:
//
//     key absent   -> the column is left alone
//     key: null    -> NULL is written to the column
//     key: value   -> value is written
//
// So a client that does `patch(url, form)` wipes every field the user did not
// fill in, returns 200, and shows a success toast. The clearing case and the
// mistake are the SAME SHAPE on the wire — which is exactly why clearing has to
// be deliberate rather than a side effect of an empty input.
//
// The rule, from docs/API-CONTRACT.md §1: build patch bodies from DIRTY FIELDS
// ONLY, never from form state.

/**
 * The dirty-field diff. Returns only the keys whose value actually changed
 * against the record as it was loaded.
 *
 * An empty string is treated as "the user cleared this field" and becomes an
 * explicit null, because a text input has no other way to express it. If a
 * genuine empty string is ever meaningful for a field, pass it through
 * `keepEmptyStrings`.
 *
 * @template {Record<string, unknown>} T
 * @param {T} original  The record as loaded from the server.
 * @param {Partial<T>} current  Current form state.
 * @param {{ keepEmptyStrings?: string[] }} [options]
 * @returns {Partial<T>} A body safe to PATCH. Empty when nothing changed.
 */
export function buildPatch(original, current, options = {}) {
    const keepEmpty = new Set(options.keepEmptyStrings ?? []);
    /** @type {Record<string, unknown>} */
    const body = {};

    for (const key of Object.keys(current)) {
        /** @type {unknown} */
        let next = current[key];

        // A cleared input is a request to write NULL, and it has to be sent as
        // an explicit null: omitting it would mean "leave it alone", which is
        // the opposite of what the user did.
        if (next === '' && !keepEmpty.has(key)) next = null;

        // Nulls are OMITTED on the wire by the server, not sent as null, so a
        // field that is currently unset arrives as an absent key. `!=` catches
        // both undefined and null on the original side, which is what makes
        // "was unset, still empty" correctly register as no change.
        const previous = original?.[key];

        // Arrays are compared BY CONTENT. Object.is on two array instances is
        // always false, so a set-valued field (a programme's markets, a
        // programme's standard codes) would be sent on every save whether or
        // not it was touched — which makes isEmptyPatch() never true and turns
        // "open the form and cancel out by saving" into a real write.
        //
        // ELEMENT-WISE AND IN ORDER, not as a set: buildPatch cannot know
        // whether a given array is an unordered set or a sequence whose order
        // is the data. A field whose order is meaningless should be kept in a
        // canonical order by the control that edits it — MarketMultiSelect
        // sorts for exactly this reason.
        const unchanged = next == null && previous == null
            ? true
            : Array.isArray(next) && Array.isArray(previous)
                ? next.length === previous.length
                    && next.every((item, i) => Object.is(item, previous[i]))
                : Object.is(next, previous);

        if (!unchanged) body[key] = next;
    }

    return /** @type {Partial<T>} */ (body);
}

/**
 * Nothing to send. A PATCH with an empty body is a wasted round trip that can
 * still fail with stale_write, so callers should skip the request entirely.
 * @param {object} body
 */
export function isEmptyPatch(body) {
    return !body || Object.keys(body).length === 0;
}

/**
 * Explicitly clear fields. Reads as the deliberate act it is at the call site,
 * rather than as an incidental null in an object literal.
 * @param {...string} fields
 * @returns {Record<string, null>}
 */
export function clearFields(...fields) {
    return Object.fromEntries(fields.map((f) => [f, null]));
}
